import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { PropertyCard } from '../PropertyCard';
import { TenantDocumentsPanel } from '../../pages/TenantDocuments';
import { MessageCenterContent } from '../../pages/Messages/MessageCenterContent';
import { DEFAULT_ISSUE_CATEGORY, ISSUE_CATEGORIES, ISSUE_CATEGORY_OPTIONS } from '../../lib/issueCategories';
import { CATEGORY_ICONS } from '../../lib/issueCategoryIcons';
import { getIssueDescriptionTemplate } from '../../lib/issueFormHelpers';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  Clock,
  ExternalLink,
  FileText,
  Home,
  Loader2,
  PoundSterling,
  Trash2,
  Wrench,
  Bell,
  Shield,
  AlertCircle,
  MessageSquare,
  X,
  Heart,
  Search,
  ArrowRight,
  Plus,
  XCircle
} from 'lucide-react';
import { scrollToElementWithOffset } from '../../lib/scroll';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getEmptyIssueForm = () => ({
  description: '',
  category: DEFAULT_ISSUE_CATEGORY,
  sub_category: '',
  custom_title: '',
  priority: 'medium',
});

const COMPLIANCE_LABELS = {
  EPC: 'Energy Performance Certificate (EPC)',
  GasSafety: 'Gas Safety Certificate',
  EICR: 'Electrical Installation Condition Report (EICR)',
  HMOLicence: 'HMO Licence',
  FireRiskAssessment: 'Fire Risk Assessment',
  DepositProtection: 'Deposit Protection Certificate',
  PrescribedInfo: 'Prescribed Information',
  TenancyAgreement: 'Tenancy Agreement',
  InventoryCheckIn: 'Inventory Check-In Report'
};

const SAFETY_COMPLIANCE_DOCUMENT_TYPES = new Set([
  'EPC',
  'GasSafety',
  'EICR',
  'HMOLicence',
  'FireRiskAssessment',
]);

const getComplianceLabel = (type) => {
  if (!type) return 'Document';
  if (COMPLIANCE_LABELS[type]) return COMPLIANCE_LABELS[type];
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getDownloadUrl = (doc) => {
  if (!doc?.download_url) return null;
  return API_URL ? `${API_URL}${doc.download_url}` : doc.download_url;
};

const getDocumentIcon = (docType) => {
  const normalized = docType.toLowerCase();
  if (normalized.includes('gas') || normalized.includes('eicr') || normalized.includes('epc')) {
    return <Shield className="h-4 w-4 text-green-600" />;
  }
  return <FileText className="h-4 w-4 text-blue-600" />;
};

const getDaysUntilExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  const now = new Date();
  const expiry = new Date(expiryDate);
  const diffTime = expiry - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const getExpiryBadge = (expiryDate, isExpired) => {
  if (isExpired) {
    return <Badge className="bg-red-100 text-red-700 text-xs">Expired</Badge>;
  }

  const daysUntilExpiry = getDaysUntilExpiry(expiryDate);
  if (daysUntilExpiry === null) return null;

  if (daysUntilExpiry <= 30) {
    return <Badge className="bg-amber-100 text-amber-700 text-xs">Expires soon</Badge>;
  }

  if (daysUntilExpiry <= 90) {
    return <Badge className="bg-blue-100 text-blue-700 text-xs">Valid</Badge>;
  }

  return <Badge className="bg-green-100 text-green-700 text-xs">Valid</Badge>;
};

const getRentStatusBadge = (status) => {
  switch (status) {
    case 'due':
      return <Badge className="bg-blue-100 text-blue-700">Due</Badge>;
    case 'overdue':
      return <Badge className="bg-red-100 text-red-700">Overdue</Badge>;
    case 'paid':
      return <Badge className="bg-green-100 text-green-700">Paid</Badge>;
    default:
      return <Badge>{status || 'Not Set'}</Badge>;
  }
};

const formatDate = (value) => {
  if (!value) return 'Not set';
  return new Date(value).toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'reported':
      return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="h-3 w-3 mr-1" /> Reported</Badge>;
    case 'in_progress':
      return <Badge className="bg-blue-100 text-blue-700"><Wrench className="h-3 w-3 mr-1" /> In Progress</Badge>;
    case 'resolved':
      return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" /> Resolved</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
};

const getApplicationStatusBadge = (status) => {
  switch (status) {
    case 'pending':
      return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
    case 'responded':
      return <Badge className="bg-blue-100 text-blue-700"><MessageSquare className="h-3 w-3 mr-1" /> Responded</Badge>;
    case 'approved':
      return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" /> Approved</Badge>;
    case 'rejected':
      return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
};

const getApplicationDecisionStatus = (application) => application?.decision_status || application?.status || 'pending';

const getPriorityBadge = (priority) => {
  switch (priority) {
    case 'high':
      return <Badge variant="destructive">High Priority</Badge>;
    case 'medium':
      return <Badge className="bg-orange-100 text-orange-700">Medium Priority</Badge>;
    case 'low':
      return <Badge className="bg-slate-100 text-slate-700">Low Priority</Badge>;
    default:
      return <Badge>{priority}</Badge>;
  }
};

export const PropertyOverviewCard = ({ propertyTitle, propertyAddress, complianceDocuments = [] }) => {
  const safetyComplianceDocuments = complianceDocuments.filter((doc) =>
    SAFETY_COMPLIANCE_DOCUMENT_TYPES.has(doc.document_type)
  );
  const tenancyDocuments = complianceDocuments.filter((doc) =>
    !SAFETY_COMPLIANCE_DOCUMENT_TYPES.has(doc.document_type)
  );

  const renderDocumentList = (docs, emptyMessage) => {
    if (docs.length === 0) {
      return (
        <p className="mt-2 text-xs text-muted-foreground">
          {emptyMessage}
        </p>
      );
    }

    return (
      <div className="mt-4 space-y-4">
        {docs.map((doc) => (
          <div key={doc.id} className="flex items-start justify-between rounded-md border bg-white p-4">
            <div className="flex items-start gap-3 flex-1">
              {getDocumentIcon(doc.document_type)}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{getComplianceLabel(doc.document_type)}</p>
                <p className="text-xs text-muted-foreground truncate">{doc.original_filename}</p>
                {doc.issue_date && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Issued: {new Date(doc.issue_date).toLocaleDateString('en-GB')}
                  </p>
                )}
                {doc.expiry_date && (
                  <p className="text-xs text-muted-foreground">
                    Expires: {new Date(doc.expiry_date).toLocaleDateString('en-GB')}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              {doc.expiry_date && getExpiryBadge(doc.expiry_date, doc.is_expired)}
              {getDownloadUrl(doc) && (
                <Button asChild variant="ghost" size="sm">
                  <a href={getDownloadUrl(doc)} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-3 w-3 mr-1" />
                    View
                  </a>
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Home className="h-5 w-5" />
          Property Overview
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm text-muted-foreground">Current Property</p>
          <p className="text-lg font-semibold text-navy-900">
            {propertyTitle || 'Assigned property'}
          </p>
          <p className="text-sm text-muted-foreground">
            {propertyAddress || 'Address will appear once assigned'}
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-royal-600">
          <CheckCircle className="h-4 w-4" />
          Active tenancy
        </div>
        <div className="rounded-md border border-royal-100 bg-royal-50/40 p-4">
          <p className="text-sm font-medium text-navy-900">Safety & MOT Documents</p>
          {renderDocumentList(safetyComplianceDocuments, 'No safety/MOT documents available yet.')}
        </div>
        <div className="rounded-md border border-royal-100 bg-royal-50/40 p-4">
          <p className="text-sm font-medium text-navy-900">Tenancy Documents</p>
          {renderDocumentList(tenancyDocuments, 'No tenancy documents available yet.')}
        </div>
      </CardContent>
    </Card>
  );
};

export const RentOverviewCard = ({ rentSchedule, rentHistory = [], marketRateShared = false }) => {
  const [marketRateSummary, setMarketRateSummary] = useState(null);

  useEffect(() => {
    let mounted = true;

    const fetchMarketRateSummary = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/tenant/market-rate-summary`, {
          withCredentials: true,
        });
        if (mounted) {
          setMarketRateSummary(response.data || null);
        }
      } catch {
        if (mounted) {
          setMarketRateSummary(null);
        }
      }
    };

    if (rentSchedule && marketRateShared) {
      fetchMarketRateSummary();
    } else {
      setMarketRateSummary(null);
    }

    return () => {
      mounted = false;
    };
  }, [marketRateShared, rentSchedule]);

  if (!rentSchedule) {
    return (
      <Card className="border-2 border-royal-100 bg-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PoundSterling className="h-5 w-5" />
            Rent Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Your landlord has not set up a rent schedule yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  const getDisplayStatus = () => {
    const status = rentSchedule.status;
    const hasRecordedPayment = Array.isArray(rentHistory) && rentHistory.length > 0;

    if (status === 'paid' || Boolean(rentSchedule.paid_date)) {
      return 'paid';
    }

    if (status === 'overdue') {
      return 'overdue';
    }

    // Use date-only comparison between today's date and next_due_date.
    const dueDate = new Date(`${rentSchedule.next_due_date}T00:00:00`);
    const today = new Date();
    const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const daysUntilDue = Math.floor((dueDate - todayDateOnly) / (1000 * 60 * 60 * 24));

    if (daysUntilDue < 0) {
      return 'overdue';
    }

    if (daysUntilDue <= 7) {
      return 'due';
    }

    // Beyond 7 days: show Paid only when there is evidence of a completed payment cycle.
    return hasRecordedPayment ? 'paid' : 'due';
  };

  const displayStatus = getDisplayStatus();
  const hasMarketData = marketRateSummary?.median_rent != null && marketRateSummary?.listings_count > 0;
  const marketDiff = hasMarketData
    ? Math.round(((rentSchedule.rent_amount / marketRateSummary.median_rent) * 100) - 100)
    : null;
  const marketUpdatedAt = marketRateSummary?.last_scraped_at
    ? new Date(marketRateSummary.last_scraped_at).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <Card className="border-2 border-royal-200 bg-white shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <PoundSterling className="h-5 w-5" />
            Rent Overview
          </span>
          {getRentStatusBadge(displayStatus)}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-3xl font-bold text-navy-900">
          £{rentSchedule.rent_amount.toLocaleString()}
          <span className="text-base font-normal text-muted-foreground"> / month</span>
          {marketDiff !== null && (
            <span className={`text-sm ml-2 ${marketDiff > 0 ? 'text-amber-600' : marketDiff < 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
              ({Math.abs(marketDiff)}% {marketDiff > 0 ? 'above' : marketDiff < 0 ? 'below' : 'at'} market)
            </span>
          )}
        </div>
        {hasMarketData && (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <PoundSterling className="h-3 w-3" />
              Area rents — £{Math.round(marketRateSummary.min_rent).toLocaleString('en-GB')} min · <span className="font-medium text-foreground">£{Math.round(marketRateSummary.median_rent).toLocaleString('en-GB')} median</span> · £{Math.round(marketRateSummary.max_rent).toLocaleString('en-GB')} max pcm
            </p>
            {marketUpdatedAt && (
              <p className="text-[11px] text-muted-foreground">
                Updated: {marketUpdatedAt}
              </p>
            )}
          </div>
        )}
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <p className="text-sm text-muted-foreground">Next due date</p>
            <p className={`font-medium ${displayStatus === 'overdue' ? 'text-red-600' : ''}`}>
              {formatDate(rentSchedule.next_due_date)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Due day</p>
            <p className="font-medium">{rentSchedule.due_day_of_month}th each month</p>
          </div>
        </div>
        {displayStatus === 'overdue' && (
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-700 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            Please arrange payment as soon as possible to avoid late fees.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const MaintenanceOverview = ({ issues, onViewAllClick, onReportIssueClick }) => {
  const summary = {
    reported: issues.filter((issue) => issue.status === 'reported').length,
    inProgress: issues.filter((issue) => issue.status === 'in_progress').length,
    resolved: issues.filter((issue) => issue.status === 'resolved').length
  };

  const recentIssues = issues.slice(0, 3);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wrench className="h-5 w-5" />
          Maintenance Overview
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 text-center sm:grid-cols-3">
          <div>
            <p className="text-sm text-muted-foreground">Reported</p>
            <p className="text-xl font-semibold">{summary.reported}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">In progress</p>
            <p className="text-xl font-semibold">{summary.inProgress}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Resolved</p>
            <p className="text-xl font-semibold">{summary.resolved}</p>
          </div>
        </div>
        {recentIssues.length > 0 ? (
          <div className="space-y-2">
            {recentIssues.map((issue) => (
              <div key={issue.id} className="rounded-md border p-4">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-navy-900">{issue.title}</p>
                  <Badge className="bg-slate-100 text-slate-700">
                    {issue.status.replace('_', ' ')}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                  <Calendar className="h-3 w-3" />
                  Reported {new Date(issue.created_at).toLocaleDateString('en-GB')}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No maintenance issues reported yet.
          </p>
        )}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button className="w-full sm:w-auto" onClick={onReportIssueClick}>
            Report an issue
          </Button>
          <Button className="w-full sm:w-auto" variant="outline" onClick={onViewAllClick}>
            View all issues
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const DocumentsSection = ({ rentHistory, onViewReceipt }) => {
  const recentReceipts = rentHistory.slice(0, 3);

  const formatReceiptMonth = (receipt) => {
    const sourceDate = receipt?.due_date || receipt?.paid_date;
    if (!sourceDate) return 'Unknown month';
    return new Date(sourceDate).toLocaleDateString('en-GB', {
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Recent Receipts
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {recentReceipts.length > 0 ? (
          recentReceipts.map((receipt) => (
            <div key={receipt.id} className="flex items-center justify-between rounded-md border p-4">
              <div>
                <p className="font-medium">Rent ({formatReceiptMonth(receipt)} - £{receipt.amount.toLocaleString()})</p>
                <p className="text-xs text-muted-foreground">
                  Paid {new Date(receipt.paid_date).toLocaleDateString('en-GB')}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewReceipt(receipt.id)}
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Receipt
              </Button>
            </div>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">No receipts available yet.</p>
        )}
      </CardContent>
    </Card>
  );
};


export const MoveIntentSection = ({ transferRequests, onRequestTransfer, onWithdrawTransferRequest, isSubmitting }) => {
  const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false);
  const [withdrawRequestId, setWithdrawRequestId] = useState(null);
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  const latestRequest = transferRequests[0];

  const handleWithdrawClick = (requestId) => {
    setWithdrawRequestId(requestId);
    setWithdrawConfirmOpen(true);
  };

  const handleConfirmWithdraw = async () => {
    setIsWithdrawing(true);
    try {
      await onWithdrawTransferRequest(withdrawRequestId);
      setWithdrawConfirmOpen(false);
      setWithdrawRequestId(null);
    } finally {
      setIsWithdrawing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Move Intent
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {latestRequest ? (
          <div className="rounded-md border p-4">
            <p className="font-medium text-navy-900">Request status</p>
            <p className="text-sm text-muted-foreground capitalize">
              {latestRequest.status.replace('_', ' ')}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Submitted {new Date(latestRequest.created_at).toLocaleDateString('en-GB')}
            </p>
            {latestRequest.status === 'pending' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleWithdrawClick(latestRequest.id)}
                className="mt-3 text-destructive hover:text-destructive"
                disabled={isWithdrawing}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Withdraw Request
              </Button>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Let us know if you are planning to move or transfer tenancies.
          </p>
        )}
        <Button
          onClick={onRequestTransfer}
          disabled={isSubmitting || latestRequest?.status === 'pending'}
        >
          {latestRequest?.status === 'pending' ? 'Request pending' : 'Request transfer'}
        </Button>
      </CardContent>

      <AlertDialog open={withdrawConfirmOpen} onOpenChange={setWithdrawConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw Transfer Request?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to withdraw this transfer request? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex justify-end gap-3">
            <AlertDialogCancel disabled={isWithdrawing}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmWithdraw} 
              disabled={isWithdrawing}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isWithdrawing ? 'Withdrawing...' : 'Withdraw'}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export const NotificationsCard = ({ notifications, onMarkAsRead, onDelete, onOpenDocuments, onOpenMessageThread, compact = false }) => {
  const tenantNotifications = notifications || [];
  const [selectedMessage, setSelectedMessage] = useState(null);

  const listContent = (
    tenantNotifications.length > 0 ? (
      <div className="space-y-4">
        {tenantNotifications.map((notif) => (
          <div
            key={notif.id}
            className={`rounded-md border p-3 sm:p-4 transition-colors cursor-pointer hover:bg-gray-50 ${notif.is_read ? 'bg-white' : 'bg-blue-50 border-blue-200'}`}
            onClick={() => {
              if (notif.thread_id) {
                onOpenMessageThread?.(notif.thread_id);
                if (!notif.is_read) onMarkAsRead?.(notif.id);
                return;
              }
              setSelectedMessage(notif);
              if (!notif.is_read) onMarkAsRead?.(notif.id);
            }}
          >
            <div className="flex items-start gap-2 sm:gap-3">
              {notif.type === 'document_shared' ? (
                <FileText className="h-4 w-4 mt-0.5 text-blue-600 flex-shrink-0" />
              ) : (
                <MessageSquare className="h-4 w-4 mt-0.5 text-blue-600 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-navy-900 text-sm break-words">
                  {notif.title}
                  {!notif.is_read && (
                    <Badge className="ml-2 bg-blue-100 text-blue-700 align-middle">New</Badge>
                  )}
                </p>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2 break-words">{notif.message}</p>
                <p className="text-xs text-muted-foreground mt-2">Click to view full message</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className="text-center py-8">
        <Bell className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-50" />
        <p className="text-sm text-muted-foreground">No notifications yet</p>
      </div>
    )
  );

  return (
    <>
      {compact ? (
        <div>{listContent}</div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Notification Centre
              {tenantNotifications.some(n => !n.is_read) && (
                <span className="ml-auto flex h-2 w-2 rounded-full bg-red-600"></span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>{listContent}</CardContent>
        </Card>
      )}

      {/* Message Detail Dialog */}
      <Dialog open={Boolean(selectedMessage)} onOpenChange={(open) => !open && setSelectedMessage(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedMessage?.title}</DialogTitle>
            <DialogDescription>
              {new Date(selectedMessage?.created_at).toLocaleString('en-GB')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <p className="text-sm text-navy-900 whitespace-pre-wrap">{selectedMessage?.message}</p>
            </div>
            <div className="flex gap-3 justify-end flex-wrap">
              {selectedMessage?.type === 'document_shared' && (
                <Button
                  onClick={() => {
                    setSelectedMessage(null);
                    onOpenDocuments?.();
                  }}
                  className="bg-royal-600 hover:bg-royal-700"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Open Documents
                </Button>
              )}
              {selectedMessage?.application_id && (
                <Button asChild className="bg-royal-600 hover:bg-royal-700">
                  <Link to="/tenant/explore#applications-section">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Application
                  </Link>
                </Button>
              )}
              <Button
                variant="outline"
                onClick={async () => {
                  if (!selectedMessage) return;
                  const deletingId = selectedMessage.id;
                  await onDelete?.(deletingId);
                  setSelectedMessage((current) =>
                    current?.id === deletingId ? null : current
                  );
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
              <Button onClick={() => setSelectedMessage(null)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const VALID_TABS = new Set(['overview', 'documents', 'messages', 'issues']);
const DEFAULT_TAB = 'overview';

export const ActiveTenancyDashboard = ({
  user,
  rentSchedules,
  rentHistory,
  issues,
  transferRequests,
  notifications = [],
  savedProperties = [],
  applications = [],
  complianceDocuments = [],
  onUnsaveSavedProperty,
  onRequestTransfer,
  onWithdrawTransferRequest,
  onWithdrawApplication,
  onMarkNotificationAsRead,
  onMarkSharedDocumentViewed,
  onDeleteNotification,
  isSubmitting,
  onViewReceipt,
  onCreateIssue
}) => {
  const tabScrollRef = useRef(null);
  const hasScrolledTabsRef = useRef(false);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = VALID_TABS.has(searchParams.get('tab')) ? searchParams.get('tab') : DEFAULT_TAB;

  const handleTabChange = useCallback((tab) => {
    if (!VALID_TABS.has(tab)) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  }, [setSearchParams]);
  const [selectedMessageThreadId, setSelectedMessageThreadId] = useState(null);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [issueFilter, setIssueFilter] = useState('all');
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueSubmitting, setIssueSubmitting] = useState(false);
  const [issueForm, setIssueForm] = useState(getEmptyIssueForm);
  // Show the current/upcoming unpaid rent, not historical paid rents
  const primaryRent = rentSchedules.find(r => r.status !== 'paid') || rentSchedules[0];
  const propertyTitle = primaryRent?.property_title || user?.property_title;
  const propertyAddress = primaryRent?.property_address;
  const issueSubCategoryOptions = ISSUE_CATEGORIES[issueForm.category] || [];
  const SelectedIssueCategoryIcon = CATEGORY_ICONS[issueForm.category];
  const routeParams = new URLSearchParams(location.search || '');
  const highlightedIssueId = routeParams.get('issueId');
  const highlightedDocumentId = routeParams.get('documentId');

  // Filter issues based on status
  const filteredIssues = issueFilter === 'all' 
    ? issues 
    : issues.filter(issue => issue.status === issueFilter);

  // Calculate statistics
  const issueStats = {
    reported: issues.filter(i => i.status === 'reported').length,
    in_progress: issues.filter(i => i.status === 'in_progress').length,
    resolved: issues.filter(i => i.status === 'resolved').length
  };

  const openApplications = applications.filter((application) => getApplicationDecisionStatus(application) === 'pending');

  const resetIssueForm = () => {
    setIssueForm(getEmptyIssueForm());
  };

  const updateIssueDetails = (updates) => {
    setIssueForm((current) => {
      const currentTemplate = getIssueDescriptionTemplate(current);
      const next = { ...current, ...updates };
      const nextTemplate = getIssueDescriptionTemplate(next);

      if (!current.description.trim() || current.description === currentTemplate) {
        return {
          ...next,
          description: nextTemplate,
        };
      }

      return next;
    });
  };

  const handleIssueDialogOpenChange = (open) => {
    setIssueDialogOpen(open);
    if (!open) {
      resetIssueForm();
    }
  };

  const handleCreateIssue = async (event) => {
    event.preventDefault();
    if (!onCreateIssue) {
      toast.error('Issue reporting is not available right now');
      return;
    }

    setIssueSubmitting(true);
    try {
      await onCreateIssue({
        description: issueForm.description,
        category: issueForm.category,
        sub_category: issueForm.sub_category || null,
        custom_title: issueForm.category === 'Other' ? (issueForm.custom_title.trim() || null) : null,
        priority: issueForm.priority,
      });
      toast.success('Issue reported successfully');
      handleIssueDialogOpenChange(false);
      handleTabChange('issues');
      setIssueFilter('all');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report issue');
    } finally {
      setIssueSubmitting(false);
    }
  };

  const handleOpenMessageThread = (threadId) => {
    if (!threadId) return;
    setSelectedMessageThreadId(threadId);
    handleTabChange('messages');
  };

  useEffect(() => {
    let isCancelled = false;

    const fetchMessageUnreadCount = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/messages/threads`, {
          withCredentials: true,
        });
        const unreadTotal = (response.data || []).reduce(
          (sum, thread) => sum + (thread?.unread_count || 0),
          0,
        );
        if (!isCancelled) {
          setMessageUnreadCount(unreadTotal);
        }
      } catch (_error) {
        if (!isCancelled) {
          setMessageUnreadCount(0);
        }
      }
    };

    fetchMessageUnreadCount();

    return () => {
      isCancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    const hashTab = location.hash?.replace('#', '');
    if (!hashTab) return;
    if (VALID_TABS.has(hashTab)) {
      handleTabChange(hashTab);
    }
  }, [location.hash, handleTabChange]);

  useEffect(() => {
    const threadId = location.state?.notificationThreadId;
    if (!threadId) return;
    setSelectedMessageThreadId(threadId);
    handleTabChange('messages');
  }, [location.state, handleTabChange]);

  useEffect(() => {
    if (highlightedIssueId) {
      setIssueFilter('all');
      handleTabChange('issues');
    } else if (highlightedDocumentId) {
      handleTabChange('documents');
    }
  }, [highlightedDocumentId, highlightedIssueId, handleTabChange]);

  useEffect(() => {
    if (!highlightedIssueId || activeTab !== 'issues') return;
    const timerId = window.setTimeout(() => {
      const element = document.getElementById(`issue-${highlightedIssueId}`);
      element?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }, 100);
    return () => window.clearTimeout(timerId);
  }, [activeTab, highlightedIssueId, issues]);

  useEffect(() => {
    if (!hasScrolledTabsRef.current) {
      hasScrolledTabsRef.current = true;
      return undefined;
    }

    if (!tabScrollRef.current) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      scrollToElementWithOffset(tabScrollRef.current);
    }, 100);

    return () => window.clearTimeout(timerId);
  }, [activeTab]);

  return (
    <div className="bg-navy-50 min-h-screen" data-testid="tenant-active-dashboard">
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold text-navy-900">
                My Tenancy
              </h1>
              <p className="text-muted-foreground">
                Manage your tenancy, applications, and property searches.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8" ref={tabScrollRef}>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="messages">
              Messages
              {messageUnreadCount > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-royal-600 text-white">
                  {messageUnreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="issues">
              Issues
              {issueStats.reported + issueStats.in_progress > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-yellow-600 text-white">
                  {issueStats.reported + issueStats.in_progress}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {applications.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Application status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {applications.map((application) => {
                    const status = getApplicationDecisionStatus(application);
                    return (
                      <div key={application.id} className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="font-medium text-sm">{application.property_title || 'Property application'}</p>
                          <p className="text-xs text-muted-foreground">
                            {status === 'approved'
                              ? 'Application approved — your active tenancy appears on this dashboard.'
                              : status === 'rejected'
                                ? 'This application was not taken forward.'
                                : 'Application pending — the landlord has not made a decision yet.'}
                          </p>
                        </div>
                        {getApplicationStatusBadge(status)}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}
            <PropertyOverviewCard
              propertyTitle={propertyTitle}
              propertyAddress={propertyAddress}
              complianceDocuments={complianceDocuments}
            />
            <RentOverviewCard rentSchedule={primaryRent} rentHistory={rentHistory} marketRateShared={Boolean(user?.market_rate_shared)} />
            <MaintenanceOverview
              issues={issues}
              onViewAllClick={() => handleTabChange('issues')}
              onReportIssueClick={() => setIssueDialogOpen(true)}
            />
            <DocumentsSection rentHistory={rentHistory} onViewReceipt={onViewReceipt} />
            {user?.move_intent_enabled && (
              <MoveIntentSection
                transferRequests={transferRequests}
                onRequestTransfer={onRequestTransfer}
                onWithdrawTransferRequest={onWithdrawTransferRequest}
                isSubmitting={isSubmitting}
              />
            )}
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            <TenantDocumentsPanel
              rentHistory={rentHistory}
              onViewReceipt={onViewReceipt}
              notifications={notifications}
              highlightedDocumentId={highlightedDocumentId}
              onMarkSharedDocumentViewed={onMarkSharedDocumentViewed}
            />
          </TabsContent>

          {/* Issues Tab */}

          {/* Issues Tab */}
          <TabsContent value="issues" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Wrench className="h-5 w-5" />
                    My Maintenance Issues
                  </CardTitle>
                  <Button size="sm" className="w-full sm:w-auto" onClick={() => setIssueDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Report Issue
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs value={issueFilter} onValueChange={setIssueFilter} className="space-y-4">
                  <TabsList>
                    <TabsTrigger value="all">
                      All ({issues.length})
                    </TabsTrigger>
                    <TabsTrigger value="reported">
                      Reported ({issueStats.reported})
                    </TabsTrigger>
                    <TabsTrigger value="in_progress">
                      In Progress ({issueStats.in_progress})
                    </TabsTrigger>
                    <TabsTrigger value="resolved">
                      Resolved ({issueStats.resolved})
                    </TabsTrigger>
                  </TabsList>

                  <div className="space-y-4">
                    {filteredIssues.length > 0 ? (
                      filteredIssues.map((issue) => (
                        <Card
                          key={issue.id}
                          id={`issue-${issue.id}`}
                          className={`p-4 ${highlightedIssueId === issue.id ? 'border-royal-500 bg-royal-50' : ''}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <h4 className="font-semibold text-navy-900">{issue.title}</h4>
                              <p className="text-sm text-muted-foreground mt-1">{issue.description}</p>
                            </div>
                            <div className="ml-4">
                              {getStatusBadge(issue.status)}
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground mt-3">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(issue.created_at).toLocaleDateString('en-GB')}
                            </span>
                            {getPriorityBadge(issue.priority)}
                          </div>
                        </Card>
                      ))
                    ) : (
                      <div className="text-center py-12">
                        <Wrench className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">
                          {issueFilter === 'all' 
                            ? 'No maintenance issues reported yet'
                            : `No ${issueFilter.replace('_', ' ')} issues`
                          }
                        </p>
                      </div>
                    )}
                  </div>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="messages" className="space-y-6">
            <MessageCenterContent
              embedded
              initialThreadId={selectedMessageThreadId}
              onUnreadCountChange={setMessageUnreadCount}
            />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={issueDialogOpen} onOpenChange={handleIssueDialogOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Report Issue</DialogTitle>
            <DialogDescription>
              Log a maintenance issue for your active tenancy without leaving this page.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateIssue} className="space-y-4">
            {propertyTitle && (
              <div className="rounded-md border bg-slate-50 p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Property</p>
                <p className="mt-1 text-sm font-medium text-navy-900">{propertyTitle}</p>
                {propertyAddress && (
                  <p className="text-xs text-muted-foreground">{propertyAddress}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={issueForm.category}
                onValueChange={(value) => updateIssueDetails({ category: value, sub_category: '', custom_title: '' })}
              >
                <SelectTrigger>
                  <SelectValue asChild>
                    <div className="flex items-center gap-2">
                      {SelectedIssueCategoryIcon ? <SelectedIssueCategoryIcon size={16} /> : null}
                      <span>{issueForm.category}</span>
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {ISSUE_CATEGORY_OPTIONS.map((category) => {
                    const Icon = CATEGORY_ICONS[category];
                    return (
                      <SelectItem key={category} value={category}>
                        <div className="flex items-center gap-2">
                          {Icon ? <Icon size={16} /> : null}
                          <span>{category}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {issueSubCategoryOptions.length > 0 && (
              <div className="space-y-2">
                <Label>Subcategory (Optional)</Label>
                <Select
                  value={issueForm.sub_category || '__none__'}
                  onValueChange={(value) => updateIssueDetails({ sub_category: value === '__none__' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {issueSubCategoryOptions.map((subCategory) => (
                      <SelectItem key={subCategory} value={subCategory}>{subCategory}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {issueForm.category === 'Other' && (
              <div className="space-y-2">
                <Label htmlFor="tenant-issue-custom-title">Custom Title</Label>
                <Input
                  id="tenant-issue-custom-title"
                  value={issueForm.custom_title}
                  onChange={(event) => updateIssueDetails({ custom_title: event.target.value })}
                  placeholder="Enter a short custom issue title"
                  required
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={issueForm.description}
                onChange={(event) => setIssueForm((current) => ({ ...current, description: event.target.value }))}
                placeholder="Describe the issue in detail..."
                rows={6}
                className="min-h-[130px]"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={issueForm.priority}
                onValueChange={(value) => setIssueForm((current) => ({ ...current, priority: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => handleIssueDialogOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" className="w-full sm:w-auto" disabled={issueSubmitting}>
                {issueSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wrench className="mr-2 h-4 w-4" />}
                Report Issue
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
};
